import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Download, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { countries, states } from '../../Components/Enums/DataEnums';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose
} from '../../Components/UI/dialog';
import { Button } from '../../Components/UI/button';
import { format } from 'date-fns';
import { getUsersArr, getConnsArr } from '../../Store/Reducers';
import { SHOW_IMPORT_INQUIRIES, canShowImportInquiries } from '../../Utils/FeatureFlags';

// Constants for better maintainability
const xnsConst = {
    sheetName: 'Transactions',
    xnsTypes: [
        'Sales Invoice',
        'Credit Note',
        'Receipt',
        'Purchase Invoice',
        'Debit Note',
        'Payment'
    ],
    maxFieldLen: {
        connection: 100,
        xnNo: 50,
        notes: 1000
    },
    mobLen: {
        india: { min: 10, max: 10 },
        international: { min: 5, max: 15 }
    },
    dateFormat: 'yyyy-MM-dd',
    templatePath: '/Templates/SahiCRM Template for Transactions.xlsx',
    expectedCol: [
        'Contact *', 'Transaction Type *', 'Transaction No *', 'Pre-Tax Amount',
        'Total Amount', 'Transaction Date *', 'Notes'
    ]
};

// Utility functions for better code organization
const DateUtils = {
    convertExcelDateToJSDate: (value) => {
        if (value instanceof Date) return format(value, xnsConst.dateFormat);
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const jsDate = new Date(Date.UTC(1899, 11, 30) + value * 86400 * 1000);
            return format(jsDate, xnsConst.dateFormat);
        }
        return null;
    },

    isValidDate: (dateString) => {
        if (!dateString) return false;
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateString)) return false;
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    },

    isDateInFuture: (dateString) => {
        if (!dateString) return false;
        const inputDate = new Date(dateString);
        const today = new Date();
        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return inputDate > today;
    }
};

const ValidationUtils = {
    validateEmail: (email) => {
        if (!email) return false;
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
        return emailRegex.test(email);
    },

    validateMobile: (mobile, isIndian) => {
        if (!mobile) return false;
        if (isIndian) {
            return mobile.length === xnsConst.mobLen.india.max && !isNaN(mobile);
        }
        const { min, max } = xnsConst.mobLen.international;
        return mobile.length >= min && mobile.length <= max && !isNaN(mobile);
    },

    validateFieldLength: (value, maxLength) => {
        return value && value.length > maxLength;
    },

    validateTransactionType: (type) => {
        if (!type) return false;
        return xnsConst.xnsTypes.includes(type);
    },

    validateAmount: (amount) => {
        if (!amount && amount !== 0) return false;
        const numAmount = parseFloat(amount);
        return !isNaN(numAmount) && numAmount >= 0;
    },

    isPaymentOrReceipt: (transactionType) => {
        if (!transactionType) return false;
        return ['Payment', 'Receipt'].includes(transactionType);
    }
};

const DataProcessingUtils = {
    getCountryState: (countryName, stateName) => {
        const countryObj = countries.find(c =>
            c.Country && countryName && c.Country.toLowerCase() === countryName.toLowerCase()
        ) || { CountryID: '', CC: '', Country: '' };

        let stateObj = null;
        if (stateName && countryObj.CountryID === 1) {
            stateObj = states.find(s =>
                s.State && s.State.toLowerCase() === stateName.toLowerCase() && s.CountryID === 1
            ) || null;
        }

        return { countryObj, stateObj };
    },

    findUserByName: (users, userName) => {
        if (!users || !userName) return null;
        return users.find(user =>
            user.name && user.name.toLowerCase() === userName.toLowerCase()
        );
    },

    findConnectionByName: (connections, connectionName) => {
        if (!connections || !connectionName) return null;
        return connections.find(conn =>
            conn.BizName && conn.BizName.toLowerCase() === connectionName.toLowerCase()
        );
    },

    sanitizeValue: (value) => {
        return value ? String(value).trim() : '';
    }
};

// Custom hooks for better separation of concerns
const useFileUpload = () => {
    const fileInputRef = useRef(null);

    const triggerFileInput = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return { fileInputRef, triggerFileInput };
};

const useExcelValidation = (users) => {
    const validateColumnHeaders = useCallback((headers) => {
        const requiredColumns = ['Contact *', 'Transaction Type *', 'Transaction No *', 'Transaction Date *'];
        const missingColumns = requiredColumns.filter(col =>
            !headers.includes(col)
        );
        return missingColumns;
    }, []);

    const validateRowData = useCallback((rowData, index) => {
        const errors = [];
        const sanitized = {};

        // Sanitize all fields
        Object.keys(rowData).forEach(key => {
            sanitized[key] = DataProcessingUtils.sanitizeValue(rowData[key]);
        });

        // Connection validation - FIXED
        const connectionName = sanitized['Contact *'];
        if (!connectionName) {
            errors.push('Contact name is required');
        } else if (ValidationUtils.validateFieldLength(connectionName, xnsConst.maxFieldLen.connection)) {
            errors.push('Contact name is too long');
        }

        // Transaction Type validation
        const transactionType = sanitized['Transaction Type *'];
        if (!transactionType) {
            errors.push('Transaction Type is required');
        } else if (!ValidationUtils.validateTransactionType(transactionType)) {
            errors.push('Invalid Transaction Type. Must be one of: ' + xnsConst.xnsTypes.join(', '));
        }

        // Invoice Number validation
        const xnNo = sanitized['Transaction No *'];
        if (!xnNo) {
            errors.push('Invoice Number is required');
        } else if (ValidationUtils.validateFieldLength(xnNo, xnsConst.maxFieldLen.xnNo)) {
            errors.push('Invoice Number is too long');
        }

        // Invoice Date validation
        const XnDate = sanitized['Transaction Date *'];
        if (!XnDate) {
            errors.push('Transaction Date is required');
        } else {
            const convertedDate = DateUtils.convertExcelDateToJSDate(XnDate);
            if (!DateUtils.isValidDate(convertedDate)) {
                errors.push('Invalid Invoice Date format (must be YYYY-MM-DD)');
            }
        }

        // Amount validations based on transaction type
        const isPaymentOrReceipt = ValidationUtils.isPaymentOrReceipt(transactionType);

        if (!isPaymentOrReceipt) {
            // For non-payment/receipt transactions, Pre Tax Amount is mandatory
            const preTaxAmount = sanitized['Pre-Tax Amount'];
            if (!preTaxAmount) {
                errors.push('Pre Tax Amount is required for this transaction type');
            } else if (!ValidationUtils.validateAmount(preTaxAmount)) {
                errors.push('Pre Tax Amount must be a valid positive number');
            }
        }

        // Total Amount validation
        const totalAmount = sanitized['Total Amount'];
        if (!totalAmount) {
            errors.push(isPaymentOrReceipt ? 'Amount is required' : 'Total Amount is required');
        } else if (!ValidationUtils.validateAmount(totalAmount)) {
            errors.push('Amount must be a valid positive number');
        }

        // Validate Total >= Pre Tax (if both present)
        if (!isPaymentOrReceipt && sanitized['Pre-Tax Amount'] && sanitized['Total Amount']) {
            const preAmount = parseFloat(sanitized['Pre-Tax Amount']);
            const totalAmountValue = parseFloat(sanitized['Total Amount']);
            if (!isNaN(preAmount) && !isNaN(totalAmountValue) && totalAmountValue < preAmount) {
                errors.push('Total Amount cannot be less than Pre Tax Amount');
            }
        }

        // Notes validation (optional)
        const notes = sanitized['Notes'];
        if (notes && ValidationUtils.validateFieldLength(notes, xnsConst.maxFieldLen.notes)) {
            errors.push('Notes are too long (max ' + xnsConst.maxFieldLen.notes + ' characters)');
        }

        return {
            errors,
            sanitizedData: sanitized,
            rowIndex: index + 1
        };
    }, [users]);

    return { validateColumnHeaders, validateRowData };
};

// Tailwind classes object for consistent styling
const styles = {
    container: 'px-4 pb-4 bg-gray-100 sahi-radius flex-1 mt-0',
    listItem: 'flex items-start gap-2',
    listNumber: 'font-semibold text-gray-800 min-w-[20px]',
    listText: 'text-gray-800',
    listHighlight: 'text-[--sahi-orange] font-medium',
    templateButton: 'text-[--sahi-dark-green] hover:text-[--sahi-primary-color] hover:text-opacity-80 underline font-medium flex items-center gap-2 mb-2 p-0',
    buttonContainer: 'flex gap-2 justify-start',
    errorContainer: 'bg-red-50 border border-red-200 rounded-lg p-4 mb-4',
    errorTitle: 'text-red-800 font-semibold mb-2 flex items-center gap-2',
    errorList: 'text-red-700 text-sm space-y-1',
    successMessage: 'bg-green-50 border border-green-200 rounded-lg p-4 text-green-800'
};

// Main component
const ImportXnsExcel = ({ open, onClose, triggerButton = true }) => {
    const navigate = useNavigate();
    const [excelData, setExcelData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const { fileInputRef, triggerFileInput } = useFileUpload();

    const users = useSelector(getUsersArr);
    const { validateColumnHeaders, validateRowData } = useExcelValidation(users);
    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setExcelData([]);
            setValidationErrors([]);
        }
    }, [open]);

    // Memoized validation checklist for better performance
    const validationChecklist = useMemo(() => [
        {
            number: 1,
            text: <>Column <span className={styles.listHighlight}>names</span> must match the template exactly.</>
        },
        {
            number: 2,
            text: <>Required fields (<span className={styles.listHighlight}>Contact, Transaction Type, Transaction No, Transaction Date</span>).</>
        },
        {
            number: 3,
            text: <><span className={styles.listHighlight}>Contact</span> must match existing contacts in your system.</>
        },
        {
            number: 4,
            text: <><span className={styles.listHighlight}>Transaction Type</span> must be one of: Sales Invoice, Credit Note, Receipt, Purchase Invoice, Debit Note, Payment.</>
        },
        {
            number: 5,
            text: <><span className={styles.listHighlight}>Pre Tax Amount</span> is mandatory for all types except Payment and Receipt.</>
        },
        {
            number: 6,
            text: <>All amounts must be <span className={styles.listHighlight}>positive numbers</span>.</>
        },
        {
            number: 7,
            text: <>Dates must be in <span className={styles.listHighlight}>YYYY-MM-DD</span> format.</>
        }
    ], []);

    const downloadTemplate = useCallback(() => {
        const link = document.createElement('a');
        link.href = xnsConst.templatePath;
        link.download = 'SahiClub Template for Transactions.xlsx';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const processExcelFile = useCallback(async (file) => {
        setIsProcessing(true);

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });

            if (!workbook.SheetNames.includes(xnsConst.sheetName)) {
                throw new Error(`Sheet "${xnsConst.sheetName}" not found. Please use the correct template.`);
            }

            const worksheet = workbook.Sheets[xnsConst.sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

            if (jsonData.length < 2) {
                throw new Error('Excel file must contain at least a header row and one data row.');
            }

            const headers = jsonData[0].map(h => String(h).trim());
            const missingColumns = validateColumnHeaders(headers);

            if (missingColumns.length > 0) {
                throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
            }

            const dataRows = jsonData.slice(1).filter(row =>
                row.some(cell => cell !== '' && cell != null)
            );

            if (dataRows.length === 0) {
                throw new Error('No valid data rows found in the Excel file.');
            }

            const allProcessedData = [];
            const errors = [];

            dataRows.forEach((row, index) => {
                const rowData = {};
                headers.forEach((header, headerIndex) => {
                    rowData[header] = row[headerIndex] || '';
                });

                const validation = validateRowData(rowData, index);

                if (validation.errors.length > 0) {
                    errors.push({
                        rowNumber: validation.rowIndex,
                        connection: validation.sanitizedData['Contact *'] || 'Unnamed Contact',
                        errors: validation.errors
                    });
                }

                // Process all data regardless of validation errors
                const isPaymentOrReceipt = ValidationUtils.isPaymentOrReceipt(validation.sanitizedData['Transaction Type *']);

                // Find connection ID
                const connectionName = validation.sanitizedData['Contact *'];

                allProcessedData.push({
                    Connection: connectionName,
                    ConnectionID: 0, // Will be resolved in correction phase
                    TransactionType: validation.sanitizedData['Transaction Type *'],
                    XnNo: validation.sanitizedData['Transaction No *'],
                    PreTaxAmount: isPaymentOrReceipt ? 0 : (parseFloat(validation.sanitizedData['Pre-Tax Amount']) || 0),
                    TotalAmount: parseFloat(validation.sanitizedData['Total Amount']) || 0,
                    XnDate: DateUtils.convertExcelDateToJSDate(validation.sanitizedData['Transaction Date *']),
                    Notes: validation.sanitizedData['Notes'] || '',
                    // Additional transaction fields
                    Type: 'TXN',
                    Status: 'Active',
                    CreatedDate: new Date().toISOString().split('T')[0]
                });
            });

            setExcelData(allProcessedData);
            setValidationErrors(errors);

            if (allProcessedData.length > 0) {
                const message = errors.length === 0
                    ? `Processed ${allProcessedData.length} rows. Ready to import.`
                    : `Processed ${allProcessedData.length} rows with ${errors.length} validation errors. Please review and correct.`;

                toast.success(message);

                navigate('/correct-excel-transactions', {
                    state: {
                        excelData: allProcessedData,
                        errors: errors,
                        transactionData: [],
                        loading: false,
                        error: null
                    }
                });
                onClose();
            } else {
                toast.error('No data found to process.');
            }

        } catch (error) {
            console.error('Excel processing error:', error);
            toast.error(error.message || 'Failed to process Excel file');
            setExcelData([]);
            setValidationErrors([]);
        } finally {
            setIsProcessing(false);
        }
    }, [validateColumnHeaders, validateRowData, navigate, onClose]);

    const handleFileUpload = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) {
            toast.error('No file selected. Please upload a valid Excel file.');
            return;
        }

        if (!file.name.match(/\.(xlsx|xls)$/)) {
            toast.error('Please upload a valid Excel file (.xlsx or .xls)');
            return;
        }

        processExcelFile(file);
    }, [processExcelFile]);

    // Don't render if not open or if imports are disabled
    if (!open || !onClose || !canShowImportInquiries()) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            {triggerButton && <DialogTrigger asChild />}
            <DialogContent className="w-[600px] max-w-[90vw] p-0 max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <div className="sm:hidden">
                        <DialogClose tabIndex={-1} />
                    </div>
                    <DialogTitle className="text-lg font-semibold">Import Transactions from Excel</DialogTitle>

                    <div className="hidden sm:block">
                        <DialogClose className="flex-shrink-0" tabIndex={-1}>
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </div>
                </DialogHeader>

                <div className={styles.container}>
                    {/* Validation Checklist */}
                    <div className="mb-6">
                        <h3 className="text-base font-semibold text-gray-800 mb-3">
                            Pre-import Checklist
                        </h3>
                        <div className="space-y-2 text-sm">
                            {validationChecklist.map(item => (
                                <div key={item.number} className={styles.listItem}>
                                    <span className={styles.listNumber}>{item.number}.</span>
                                    <div className={styles.listText}>{item.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Download Template Button */}
                    <div className="mb-4">
                        <Button
                            variant="link"
                            onClick={downloadTemplate}
                            className={styles.templateButton}
                            disabled={isProcessing}
                        >
                            <Download className="h-4 w-4 text-[--sahi-primary-color]" />
                            Download Template
                        </Button>
                    </div>



                    {/* Validation Errors Display */}
                    {validationErrors.length > 0 && (
                        <div className={styles.errorContainer}>
                            <div className={styles.errorTitle}>
                                <AlertCircle className="h-5 w-5" />
                                Validation Errors ({validationErrors.length} rows)
                            </div>
                            <div className={styles.errorList}>
                                {validationErrors.slice(0, 10).map((error, index) => (
                                    <div key={index} className="border-l-2 border-red-300 pl-3 py-1">
                                        <div className="font-medium">
                                            Row {error.rowNumber}: {error.connection}
                                        </div>
                                        <ul className="ml-4 list-disc">
                                            {error.errors.map((err, errIndex) => (
                                                <li key={errIndex}>{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                                {validationErrors.length > 10 && (
                                    <div className="text-red-600 font-medium mt-2">
                                        ... and {validationErrors.length - 10} more errors
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {excelData.length > 0 && validationErrors.length === 0 && (
                        <div className={styles.successMessage}>
                            ✓ Successfully processed {excelData.length} rows. Ready for import.
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={styles.buttonContainer}>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                        <Button
                            onClick={triggerFileInput}
                            disabled={isProcessing}
                            className="flex items-center gap-2"
                        >
                            {isProcessing ? (
                                <>
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4" />
                                    Import Excel
                                </>
                            )}
                        </Button>
                        <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ImportXnsExcel;